// CTW-DURMAH-STUDENT-MANUAL-01:
// Shared presentation primitives for the Student Manual.
//
// Modelled on src/components/resources/ResourcePrimitives.tsx for house
// consistency, but parameterised where the resources versions hard-code
// /resources as the parent.

import React from "react";
import Link from "next/link";
import {
  AlertTriangle,
  ArrowRight,
  ChevronRight,
  Info,
  Lightbulb,
  Lock,
  ShieldCheck,
} from "lucide-react";
import type { ManualBlock, ManualSection } from "@/content/manual";

/* ------------------------------------------------------------------ */
/* Breadcrumbs                                                         */
/* ------------------------------------------------------------------ */

export function ManualBreadcrumbs({
  category,
  current,
}: {
  category?: { label: string; id: string } | null;
  current: string;
}) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex flex-wrap items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-slate-500 mb-6"
    >
      <Link
        href="/manual"
        prefetch={false}
        className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
      >
        Student Manual
      </Link>
      {category ? (
        <>
          <ChevronRight className="w-3 h-3" aria-hidden="true" />
          <Link
            href={`/manual#${category.id}`}
            prefetch={false}
            className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          >
            {category.label}
          </Link>
        </>
      ) : null}
      <ChevronRight className="w-3 h-3" aria-hidden="true" />
      <span className="text-gray-600 dark:text-slate-300 normal-case tracking-normal font-semibold">
        {current}
      </span>
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/* Callouts                                                            */
/* ------------------------------------------------------------------ */

const CALLOUT_STYLES: Record<
  "note" | "tip" | "boundary" | "safety",
  { wrap: string; icon: React.ReactNode; label: string }
> = {
  note: {
    wrap:
      "bg-slate-50 dark:bg-slate-900/50 border-slate-300 dark:border-slate-700",
    icon: <Info className="w-4 h-4 text-slate-500" aria-hidden="true" />,
    label: "Note",
  },
  tip: {
    wrap:
      "bg-indigo-50 dark:bg-indigo-950/30 border-indigo-400 dark:border-indigo-500/60",
    icon: (
      <Lightbulb className="w-4 h-4 text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
    ),
    label: "Tip",
  },
  boundary: {
    wrap:
      "bg-amber-50 dark:bg-amber-950/20 border-amber-400 dark:border-amber-500/60",
    icon: (
      <ShieldCheck className="w-4 h-4 text-amber-600 dark:text-amber-400" aria-hidden="true" />
    ),
    label: "Where Durmah draws the line",
  },
  safety: {
    wrap:
      "bg-rose-50 dark:bg-rose-950/20 border-rose-400 dark:border-rose-500/60",
    icon: (
      <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400" aria-hidden="true" />
    ),
    label: "Important",
  },
};

function Callout({
  tone,
  title,
  text,
}: {
  tone: "note" | "tip" | "boundary" | "safety";
  title?: string;
  text: string;
}) {
  const style = CALLOUT_STYLES[tone];
  return (
    <div className={`my-6 rounded-2xl border-l-4 px-5 py-4 ${style.wrap}`}>
      <div className="flex items-center gap-2 mb-1.5">
        {style.icon}
        <span className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-slate-400">
          {title || style.label}
        </span>
      </div>
      <p className="text-sm leading-relaxed text-gray-700 dark:text-slate-300 m-0">
        {text}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Block renderer                                                      */
/* ------------------------------------------------------------------ */

export function ManualBlocks({ blocks }: { blocks: ManualBlock[] }) {
  return (
    <div className="space-y-1">
      {blocks.map((block, index) => {
        switch (block.kind) {
          case "heading":
            return (
              <h2
                key={index}
                id={block.id}
                className="scroll-mt-32 text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4"
              >
                {block.text}
              </h2>
            );

          case "para":
            return (
              <p
                key={index}
                className="text-[15px] leading-relaxed text-gray-700 dark:text-slate-300 mb-4"
              >
                {block.text}
              </p>
            );

          case "steps":
            return (
              <div key={index} className="mb-6">
                {block.intro ? (
                  <p className="text-[15px] leading-relaxed text-gray-700 dark:text-slate-300 mb-3">
                    {block.intro}
                  </p>
                ) : null}
                <ol className="space-y-3">
                  {block.items.map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-black flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <span className="text-[15px] leading-relaxed text-gray-700 dark:text-slate-300">
                        {item}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            );

          case "bullets":
            return (
              <div key={index} className="mb-6">
                {block.intro ? (
                  <p className="text-[15px] leading-relaxed text-gray-700 dark:text-slate-300 mb-3">
                    {block.intro}
                  </p>
                ) : null}
                <ul className="space-y-2">
                  {block.items.map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <span
                        className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2.5"
                        aria-hidden="true"
                      />
                      <span className="text-[15px] leading-relaxed text-gray-700 dark:text-slate-300">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );

          case "table":
            return (
              <div key={index} className="mb-6">
                {block.intro ? (
                  <p className="text-[15px] leading-relaxed text-gray-700 dark:text-slate-300 mb-3">
                    {block.intro}
                  </p>
                ) : null}
                <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-slate-800">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-slate-900">
                        {block.headers.map((h, i) => (
                          <th
                            key={i}
                            scope="col"
                            className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-slate-400 whitespace-nowrap"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {block.rows.map((row, ri) => (
                        <tr
                          key={ri}
                          className="border-t border-gray-100 dark:border-slate-800/70"
                        >
                          {row.map((cell, ci) => (
                            <td
                              key={ci}
                              className={`px-4 py-3 align-top leading-relaxed ${
                                ci === 0
                                  ? "font-semibold text-gray-900 dark:text-white"
                                  : "text-gray-600 dark:text-slate-400"
                              }`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );

          case "callout":
            return (
              <Callout
                key={index}
                tone={block.tone}
                title={block.title}
                text={block.text}
              />
            );

          case "quote":
            return (
              <figure
                key={index}
                className="my-6 rounded-2xl bg-gray-900 dark:bg-black/40 px-5 py-4 border border-gray-800"
              >
                {block.attribution ? (
                  <figcaption className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2">
                    {block.attribution}
                  </figcaption>
                ) : null}
                <blockquote className="text-sm leading-relaxed text-gray-200 italic m-0">
                  {block.text}
                </blockquote>
              </figure>
            );

          default:
            return null;
        }
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Member gate                                                         */
/* ------------------------------------------------------------------ */

/**
 * Shown in place of a member-only section body for signed-out visitors.
 * Deliberately states what is behind the gate — a teaser that hides what it
 * is hiding is just an obstacle.
 */
export function ManualMemberGate({ section }: { section: ManualSection }) {
  return (
    <div className="my-8 rounded-3xl border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/60 dark:bg-indigo-950/20 p-8 text-center">
      <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center mx-auto mb-4">
        <Lock className="w-5 h-5 text-white" aria-hidden="true" />
      </div>
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
        Sign in to read this section
      </h2>
      <p className="text-sm leading-relaxed text-gray-600 dark:text-slate-400 max-w-md mx-auto mb-6">
        {section.summary} This part of the manual covers exact limits and
        in-product detail, so it is written for students who are signed in and
        using Durmah.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Link
          href="/login"
          prefetch={false}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors"
        >
          Sign in <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Link>
        <Link
          href="/pricing"
          prefetch={false}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-300 text-sm font-bold hover:border-indigo-400 transition-colors"
        >
          Start a free trial
        </Link>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Help footer — on every manual page                                  */
/* ------------------------------------------------------------------ */

export function ManualHelpFooter() {
  return (
    <div className="mt-14 rounded-3xl bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-800 p-8">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
        Still stuck?
      </h2>
      <p className="text-sm leading-relaxed text-gray-600 dark:text-slate-400 mb-5">
        Ask Durmah — it knows this manual and can point you to the right
        section. If the manual itself is wrong, out of date or unclear, tell us
        and we will fix it. Documentation errors are treated as product bugs.
      </p>
      <div className="flex flex-wrap gap-3">
        <Link
          href="/support"
          prefetch={false}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-bold hover:opacity-90 transition-opacity"
        >
          Contact support
        </Link>
        <Link
          href="/feedback"
          prefetch={false}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-300 text-sm font-bold hover:border-indigo-400 transition-colors"
        >
          Share feedback on this page
        </Link>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Disclaimer                                                          */
/* ------------------------------------------------------------------ */

export function ManualDisclaimer() {
  return (
    <p className="mt-10 text-xs leading-relaxed text-gray-400 dark:text-slate-600">
      Durmah is an independent study platform and is not affiliated with,
      endorsed by, or operated on behalf of any university. Everything in this
      manual describes study support for legal education. It is not legal
      advice. Where this manual and your module handbook disagree, your module
      handbook is authoritative.
    </p>
  );
}
